import type { ReactNode } from 'react'
import styles from './OpportunityItem.module.css'
import cx from 'classnames'
import { Link } from '@/components/Link'
import { Icon } from '@/components/Icon'

interface OpportunityItemProps {
  children?: ReactNode
  isDisabled: boolean
  selected: boolean
  onSelect: () => unknown
  testId?: string
}

export function OpportunityItem({
  children,
  isDisabled,
  selected,
  onSelect,
  testId,
}: OpportunityItemProps) {
  return (
    <Link
      className={cx(
        styles.root,
        isDisabled && styles.disabled,
        selected && styles.selected,
      )}
      onClick={() => {
        if (!isDisabled) {
          onSelect()
        }
      }}
      data-test={testId}
    >
      {children}
      <div className={styles.shadow} />
    </Link>
  )
}

interface OpportunityItemAvatarInfoProps {
  children?: ReactNode
}

export function OpportunityItemAvatarInfo({
  children,
}: OpportunityItemAvatarInfoProps) {
  return <div className={styles.avatarAndInfo}>{children}</div>
}

interface OpportunityItemAvatarProps {
  isDisabled: boolean
  avatar: ReactNode
  secondaryAvatar?: ReactNode
}

export function OpportunityItemAvatar({
  isDisabled,
  avatar,
  secondaryAvatar,
}: OpportunityItemAvatarProps) {
  return (
    <div className={cx(styles.avatarContainer, isDisabled && styles.disabled)}>
      {avatar}
      {secondaryAvatar && (
        <div className={styles.secondaryAvatar}>{secondaryAvatar}</div>
      )}
    </div>
  )
}

interface OpportunityItemInfoProps {
  children?: ReactNode
}

export function OpportunityItemInfo({ children }: OpportunityItemInfoProps) {
  return (
    <div className={styles.info}>
      <div className={styles.primaryInfo}>{children}</div>
    </div>
  )
}

interface OpportunityItemTitleProps {
  title: string
  secondaryTitle?: string
  isDisabled: boolean
}

export function OpportunityItemTitle({
  title,
  secondaryTitle,
  isDisabled,
}: OpportunityItemTitleProps) {
  return (
    <p className={styles.title}>
      {title}
      {secondaryTitle && (
        <span>
          <span className={styles.titleSeparator}> • </span>
          <span
            className={cx(styles.secondaryTitle, isDisabled && styles.disabled)}
          >
            {secondaryTitle}
          </span>
        </span>
      )}
    </p>
  )
}

interface OpportunityItemSubtitleProps {
  children?: ReactNode
}

export function OpportunityItemSubtitle({
  children,
}: OpportunityItemSubtitleProps) {
  return <p className={styles.subtitle}>{children}</p>
}

interface ForwardIconProps {
  isDisabled: boolean
  selectColor?: 'blue' | 'green' | 'red'
}

export function ForwardIcon({
  isDisabled = false,
  selectColor = 'blue',
}: ForwardIconProps) {
  return (
    <div
      className={cx(
        styles.forwardIconContainer,
        selectColor && styles[selectColor],
      )}
    >
      {!isDisabled && <Icon name="arrow" />}
    </div>
  )
}
